<template>
  <div :key="note.id" class="note-actions d-flex align-center">
    <div class="actions-left d-flex align-center">
      <v-tooltip
        v-if="showFavorite"
        location="top" :open-delay="300" content-class="text-neutral0 bg-neutral900"
        :disabled="mobile || !hasHover"
      >
        <template #activator="{ props }">
          <BrandButton
            class="actions-btn"
            size="small" color="neutral0" icon flat
            :loading="note.loading?.favorite"
            v-bind="props"
            @click.stop.prevent="$emit('favorite')"
          >
            <PhStar class="text-h5 text-neutral900" :weight="note.isFavorite ? 'fill' : 'regular'" />
          </BrandButton>
        </template>
        <span>{{ t('cta.favorite') }}</span>
      </v-tooltip>

      <template v-if="showSecondary">
        <v-tooltip location="top" :open-delay="300" content-class="text-neutral0 bg-neutral900" :disabled="mobile">
          <template #activator="{ props }">
            <BrandButton
              v-if="note.isCollectionItem || publicMode"
              class="actions-btn"
              size="small" flat
              color="neutral0" icon v-bind="props" @click.stop.prevent="$emit('save')"
            >
              <PhPlus class="text-h5 text-neutral900" />
            </BrandButton>
            <BrandButton
              v-else
              class="actions-btn"
              size="small" flat
              color="neutral0" icon v-bind="props" @click.stop.prevent="$emit('add-to-collection')"
            >
              <PhListPlus class="text-h5 text-neutral900" />
            </BrandButton>
          </template>
          <span>{{ t(note.isCollectionItem || publicMode ? 'cta.add_to_bookmarks' : 'cta.add_to_collection') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="(note.isCollectionItem && !publicMode) || (publicMode && canCopy)" location="top" :open-delay="300"
          content-class="text-neutral0 bg-neutral900"
          :disabled="mobile"
        >
          <template #activator="{ props }">
            <BrandButton
              class="actions-btn"
              size="small" flat
              color="neutral0" icon v-bind="props" @click.stop.prevent="$emit('add-to-collection-from-collection')"
            >
              <PhListPlus class="text-h5 text-neutral900" />
            </BrandButton>
          </template>
          <span>{{ t('cta.add_to_collection') }}</span>
        </v-tooltip>
      </template>

      <template v-if="showComments">
        <v-tooltip location="top" :open-delay="300" content-class="text-neutral0 bg-neutral900" :disabled="mobile">
          <template #activator="{ props: tooltipProps }">
            <BrandButton
              class="actions-btn text-neutral900"
              size="small" color="neutral0" flat v-bind="tooltipProps"
              @click.stop.prevent="onCommentsClick"
            >
              <template #prepend>
                <BrandBadge :model-value="note.hasUnreadComments">
                  <PhChatCenteredText class="text-h5 text-neutral900" />
                </BrandBadge>
              </template>
              {{ note.commentsCount }}
            </BrandButton>
          </template>
          <span>{{ t(note.isCollectionItem ? 'common.comments' : 'common.notes', { count: 2 }) }}</span>
        </v-tooltip>
      </template>

      <template v-if="showLikes">
        <v-tooltip location="top" :open-delay="300" content-class="text-neutral0 bg-neutral900" :disabled="mobile || !hasHover">
          <template #activator="{ props }">
            <BrandButton
              v-if="note.isCollectionItem || publicMode"
              class="ml-n2 actions-btn text-neutral900"
              :loading="note.loading?.like" size="small"
              color="neutral0" flat v-bind="props" @click.stop.prevent="$emit('like')"
            >
              <template #prepend>
                <PhThumbsUp class="text-h5 text-neutral900" :weight="note.isLikedByUser ? 'fill' : 'regular'" />
              </template>
              <span
                class="likes-count text-neutral900"
                :class="{'text-decoration-underline': note.likes > 0, 'cursor-default': note.likes === 0}"
                @click="onLikesClick"
              >
                {{ note.likes }}
              </span>
            </BrandButton>
          </template>
          <span>{{ t('cta.like') }}</span>
        </v-tooltip>
      </template>
    </div>

    <div class="actions-right d-flex align-center">
      <v-tooltip
        v-if="showFilledPin"
        location="top" :open-delay="300" content-class="text-neutral0 bg-neutral900" :disabled="mobile"
      >
        <template #activator="{ props: tooltipProps }">
          <BrandButton
            class="actions-btn"
            size="small" color="neutral0" icon flat
            v-bind="tooltipProps"
            :style="isPinDisabled ? 'cursor: default; pointer-events: none' : ''"
            @click.stop.prevent="!isPinDisabled && $emit('pin-edit')"
          >
            <PhPushPin weight="fill" class="text-h6 text-neutral900" />
          </BrandButton>
        </template>
        <span>{{ t('pin.edit_position') }}</span>
      </v-tooltip>

      <v-tooltip
        v-else-if="showOutlinePin"
        location="top" :open-delay="300" content-class="text-neutral0 bg-neutral900" :disabled="mobile"
      >
        <template #activator="{ props: tooltipProps }">
          <BrandButton
            class="actions-btn"
            size="small" color="neutral0" icon flat
            v-bind="tooltipProps"
            :style="isBulkActionsActive ? 'cursor: default; pointer-events: none' : ''"
            @click.stop.prevent="!isBulkActionsActive && $emit('pin-edit')"
          >
            <PhPushPin weight="regular" class="text-h6 text-neutral900" />
          </BrandButton>
        </template>
        <span>{{ t('cta.pin_item') }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { useRouter, useRoute } from 'vue-router'
import {
  PhStar, PhChatCenteredText, PhPlus, PhListPlus, PhThumbsUp, PhPushPin,
} from '@phosphor-icons/vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import {
  canPerformItemAction,
  buildPermissionContext,
  CollectionItemAction,
  getItemPinDisplay,
} from '@/utils/permissions/collection-item'
import { useHasHover } from '@/composables/useHasHover'

const { t } = useI18n()
const { mobile } = useDisplay()
const { hasHover } = useHasHover()
const router = useRouter()
const route = useRoute()
const { isBulkActionsActive } = useBulkActionsV2()

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
  hidePrimaryAction: Boolean,
  hideSecondaryAction: Boolean,
  hideThirdAction: Boolean,
})

const permissionContext = computed(() => {
  return buildPermissionContext({
    membership: props.membership,
    item: props.note,
    userId: props.userId,
    collection: props.publicMode ? { isPublic: true } : undefined,
  })
})

const canLike = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.LIKE))
const canReadComments = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.READ_COMMENTS))
const canReadLikes = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.READ_LIKES))
const canCopy = computed(() => canPerformItemAction(permissionContext.value, CollectionItemAction.COPY_TO_COLLECTION))

const pinDisplay = computed(() => getItemPinDisplay({
  context: permissionContext.value,
  isPinned: props.note.pinPosition != null,
  publicMode: props.publicMode,
}))
const canPin = computed(() => pinDisplay.value.canPin)
const showFilledPin = computed(() => pinDisplay.value.showFilledPin)
const showOutlinePin = computed(() => pinDisplay.value.showOutlinePin)
const isPinDisabled = computed(() => !canPin.value || isBulkActionsActive.value)

const showFavorite = computed(() => !props.publicMode && !props.note.isCollectionItem)
const showSecondary = computed(() => {
  if (props.hideSecondaryAction) return false
  if (props.publicMode) return canCopy.value
  return true
})
const showComments = computed(() => {
  if (props.hidePrimaryAction) return false
  if (props.publicMode) return canReadComments.value
  return true
})
const showLikes = computed(() => {
  if (props.hideThirdAction) return false
  if (props.publicMode) return canReadLikes.value || canLike.value
  return true
})

const emit = defineEmits([
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'like',
  'likes',
  'pin-edit',
])

function onLikesClick(e) {
  if (props.note.likes > 0) {
    e.stopPropagation()
    e.preventDefault()
    emit('likes')
  }
}

function onCommentsClick() {
  if (props.publicMode) {
    const { username, id } = route.params
    router.push(`/u/${username}/collections/${id}/notes/${props.note.id}`)
    return
  }
  if (props.note.isPrivate) {
    router.push(`/notes/${props.note.id}`)
  } else {
    router.push(`/collections/${props.note.ownerId}/notes/${props.note.id}`)
  }
}
</script>

<style lang="scss" scoped>
.note-actions {
  gap: 0;
}

.actions-left {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  gap: 12px;
}

.actions-right {
  flex-shrink: 0;
  margin-left: auto;
  gap: 4px;
}

.actions-btn {
  height: 28px;
  width: 28px;
}

.likes-count {
  padding: 8px;
  margin-left: -8px;
}
</style>
